"use client";

import { useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

import {
	getLongestMoneySegmentLength,
	withBreakableCurrencySpacing,
} from "@/lib/pricing/breakable-money-label";
import { cn } from "@/lib/utils";
import { useAtcStatus } from "./add-to-cart-form";

export interface QuantityOption {
	qty: number;
	totalLabel: string;
	perUnitLabel: string;
	savingsLabel?: string;
	available?: boolean;
	doseLabel?: string | null;
	/**
	 * Saleor variant id for pack-variant products (where qty == variant).
	 * When present, clicking the option navigates to ?variant=<id>; otherwise the
	 * picker uses internal state and writes a hidden <input name="quantity">.
	 */
	variantId?: string;
}

interface QuantityPickerProps {
	options: QuantityOption[];
	initialQty?: number;
	/** Called when the user picks a qty (only fires in form-input mode). */
	onChange?: (qty: number) => void;
	pieceLabel: string;
	/** Optional plural provided by the localized server component. */
	pluralPieceLabel?: string;
	perUnitSuffix: string;
	inputName?: string;
	/**
	 * For pack-variant products, the controller passes the currently-selected
	 * variantId so we know which option to highlight as active. Otherwise
	 * `initialQty` is used.
	 */
	selectedVariantId?: string;
	productSlug: string;
	channel: string;
}

const COLS_BY_COUNT: Record<number, string> = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-2 @[17.5rem]/quantity:grid-cols-3",
	4: "grid-cols-2 @lg/quantity:grid-cols-4",
};

const WIDE_COLS_BY_COUNT: Record<number, string> = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-2 @md/quantity:grid-cols-3",
	4: "grid-cols-2 @xl/quantity:grid-cols-4",
};

const EXTRA_WIDE_COLS_BY_COUNT: Record<number, string> = {
	1: "grid-cols-1",
	2: "grid-cols-1 @sm/quantity:grid-cols-2",
	3: "grid-cols-1 @sm/quantity:grid-cols-2 @2xl/quantity:grid-cols-3",
	4: "grid-cols-1 @sm/quantity:grid-cols-2 @5xl/quantity:grid-cols-4",
};

export function QuantityPicker({
	options,
	initialQty = 1,
	onChange,
	pieceLabel,
	pluralPieceLabel,
	perUnitSuffix,
	inputName = "quantity",
	selectedVariantId,
	productSlug,
	channel,
}: QuantityPickerProps) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [isPending, startTransition] = useTransition();

	// Pack-variant mode: any option carries a variantId → URL-driven, no internal state.
	const isVariantMode = options.some((o) => o.variantId);
	const [selectedQty, setSelectedQty] = useState(initialQty);
	const atcStatus = useAtcStatus();

	const activeQty = isVariantMode
		? options.find((o) => o.variantId === selectedVariantId)?.qty
		: (atcStatus?.quantity ?? selectedQty);

	const select = (opt: QuantityOption) => {
		if (opt.available === false) return;
		if (isVariantMode && opt.variantId) {
			if (opt.variantId === selectedVariantId) return;
			posthog.capture("variant_selected", {
				product_slug: productSlug,
				channel,
				previous_variant_id: selectedVariantId,
				variant_id: opt.variantId,
				selection_dimension: "pack",
				dose: opt.doseLabel ?? undefined,
				pack_count: opt.qty,
				available: true,
			});
			const params = new URLSearchParams(searchParams);
			params.set("variant", opt.variantId);
			startTransition(() => {
				router.replace(`${pathname}?${params.toString()}`, { scroll: false });
			});
		} else {
			if (opt.qty === activeQty) return;
			setSelectedQty(opt.qty);
			atcStatus?.setQuantity(opt.qty);
			onChange?.(opt.qty);
			posthog.capture("pdp_quantity_selected", {
				product_slug: productSlug,
				channel,
				variant_id: selectedVariantId,
				previous_quantity: activeQty,
				quantity: opt.qty,
			});
		}
	};

	// Long reference-currency amounts need wider cards; keep their grouped digits intact.
	const longestMoneySegment = options.reduce(
		(longest, opt) =>
			Math.max(
				longest,
				getLongestMoneySegmentLength(opt.totalLabel),
				getLongestMoneySegmentLength(opt.perUnitLabel),
				getLongestMoneySegmentLength(opt.savingsLabel ? `−${opt.savingsLabel}` : ""),
			),
		0,
	);
	const columnClasses =
		longestMoneySegment >= 14
			? EXTRA_WIDE_COLS_BY_COUNT
			: longestMoneySegment >= 10
				? WIDE_COLS_BY_COUNT
				: COLS_BY_COUNT;
	const colsClass = columnClasses[options.length] ?? columnClasses[3];
	const hasSavings = options.some((opt) => opt.savingsLabel);

	return (
		<div className="@container/quantity">
			{/* Always render the hidden input (even in variant mode where it's harmless)
			    to keep server/client trees identical — conditional render here was
			    triggering a hydration mismatch. */}
			<input type="hidden" name={inputName} value={isVariantMode ? 1 : (activeQty ?? selectedQty ?? 1)} />
			<div role="radiogroup" aria-label={pieceLabel} className={cn("grid auto-rows-fr gap-2", colsClass)}>
				{options.map((opt) => {
					const active = opt.qty === activeQty;
					const available = opt.available !== false;
					return (
						<button
							key={opt.variantId ?? opt.qty}
							type="button"
							role="radio"
							aria-checked={active}
							aria-disabled={!available || isPending}
							disabled={!available || isPending}
							onClick={() => select(opt)}
							className={cn(
								"flex min-w-0 flex-col gap-0.5 rounded-xl border-2 p-2 text-left transition-colors @sm/quantity:p-3",
								active
									? "border-primary bg-primary text-primary-foreground"
									: "hover:border-primary/40 border-border bg-card text-foreground",
								!available && "cursor-not-allowed line-through opacity-50",
							)}
						>
							<span className="max-w-full text-xs font-bold">
								{opt.qty} {opt.qty === 1 || !pluralPieceLabel ? pieceLabel : pluralPieceLabel}
							</span>
							<span className="max-w-full text-sm font-extrabold @sm/quantity:text-base">
								{withBreakableCurrencySpacing(opt.totalLabel)}
							</span>
							<span className={cn("max-w-full text-xs", active ? "opacity-80" : "text-muted-foreground")}>
								{withBreakableCurrencySpacing(opt.perUnitLabel)}
								<wbr />
								{perUnitSuffix}
							</span>
							{hasSavings && (
								<span className="mt-auto min-h-6 max-w-full pt-1">
									{opt.savingsLabel && (
										<span
											className={cn(
												"inline-block max-w-full rounded px-1.5 py-0.5 text-[11px] font-semibold leading-4",
												active
													? "bg-[color-mix(in_oklab,var(--primary-foreground)_12%,transparent)] text-primary-foreground"
													: "bg-muted text-muted-foreground",
											)}
										>
											−{withBreakableCurrencySpacing(opt.savingsLabel)}
										</span>
									)}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}
